import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, AlertCircle, ClipboardPlus, Download, Search, Settings } from 'lucide-react';
import { tasksApi, equipmentApi, usersApi, type MaintenanceTask } from '@/lib/api';
import { exportToExcel, exportToCsv, downloadMaintenanceTaskTemplate } from '@/lib/excel';
import { MaintenanceTaskImport } from '@/components/MaintenanceTaskImport';
import { fmtDateTime } from '@/lib/date';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea } from '@/components/ui/input';
import { Badge, taskTypeColor } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Card, CardBody } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';

const BLANK: Partial<MaintenanceTask> = {
  name: '', task_type: 'preventive', frequency_type: 'days',
  frequency_value: undefined, estimated_duration_minutes: undefined,
  instructions: '', is_active: true,
  equipment_id: '', assigned_technician_id: '',
  schedule_type: 'time',
  meter_interval: undefined,
  last_trigger_reading: undefined,
};

function isDue(t: MaintenanceTask) {
  if (!t.next_due_at) return false;
  return new Date(t.next_due_at) <= new Date();
}

export function SchedulesPage() {
  const { isAdmin, isSupervisor, timezone, hasPro } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MaintenanceTask | null>(null);
  const [detail, setDetail] = useState<MaintenanceTask | null>(null);
  const [form, setForm] = useState<Partial<MaintenanceTask>>(BLANK);
  // Create mode can fan one PM out across several machines ("line" PM).
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  const [equipFilter, setEquipFilter] = useState('');
  // Edit mode: when a task belongs to a line group, apply changes to the whole set.
  const [applyToGroup, setApplyToGroup] = useState(false);
  const [filterActive, setFilterActive] = useState<'true' | 'false' | ''>('true');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', page, filterActive, search],
    queryFn: () => tasksApi.list(page, 50, undefined, undefined,
      filterActive === '' ? undefined : filterActive === 'true',
      search || undefined),
  });

  const { data: equipment } = useQuery({
    queryKey: ['equipment', 'all'],
    queryFn: () => equipmentApi.list(1, 200),
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(1),
    enabled: isAdmin,
  });

  const save = useMutation({
    mutationFn: async () => {
      const isMeter = form.schedule_type === 'meter';
      const payload = {
        ...form,
        // For meter-based: frequency_value is meaningless, frequency_type stays as a label only.
        // For time-based: clear meter fields.
        frequency_value: isMeter ? null : (form.frequency_type === 'on_condition' ? null : form.frequency_value),
        meter_interval: isMeter ? form.meter_interval : null,
        last_trigger_reading: isMeter ? (form.last_trigger_reading ?? 0) : null,
        assigned_technician_id: form.assigned_technician_id || null,
      };
      if (editing) {
        // Apply shared fields across the whole line when requested.
        if (editing.pm_group_id && applyToGroup) {
          await tasksApi.updateGroup(editing.pm_group_id, {
            name: payload.name,
            task_type: payload.task_type,
            frequency_type: payload.frequency_type,
            frequency_value: payload.frequency_value,
            estimated_duration_minutes: payload.estimated_duration_minutes,
            assigned_technician_id: payload.assigned_technician_id,
            instructions: payload.instructions,
            compliance_window_days: payload.compliance_window_days,
            month_week: payload.month_week,
            month_weekday: payload.month_weekday,
            is_active: payload.is_active,
          });
          return;
        }
        await tasksApi.update(editing.id, payload);
        return;
      }
      // Create: fan out across the selected machines (one task each, shared group).
      const { equipment_id: _omit, ...groupPayload } = payload;
      await tasksApi.createGroup({ ...groupPayload, equipment_ids: equipmentIds });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); closeDialog(); },
  });

  const del = useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const [createWoError, setCreateWoError] = useState<string | null>(null);
  const createWO = useMutation({
    mutationFn: (id: string) => tasksApi.createWO(id),
    onSuccess: (wo) => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setCreateWoError(null);
      alert(`Work order ${wo.wo_number} created successfully.`);
    },
    onError: (err: Error) => setCreateWoError(err.message),
  });

  const openCreate = () => { setEditing(null); setForm(BLANK); setEquipmentIds([]); setEquipFilter(''); setApplyToGroup(false); setOpen(true); };
  const openEdit = (t: MaintenanceTask) => {
    setEditing(t);
    setApplyToGroup(false);
    // Prisma Decimal fields come back as strings; cast to number for the form
    // so the numeric inputs and the save payload stay in sync.
    setForm({
      ...t,
      meter_interval:       t.meter_interval       != null ? Number(t.meter_interval)       : undefined,
      last_trigger_reading: t.last_trigger_reading != null ? Number(t.last_trigger_reading) : undefined,
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); setForm(BLANK); setEquipmentIds([]); setEquipFilter(''); setApplyToGroup(false); };
  const set = (k: keyof MaintenanceTask, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const totalCount = data?.pagination.total ?? 0;
  const overdueCount = data?.data.filter(isDue).length ?? 0;
  const tagText = totalCount === 0
    ? undefined
    : overdueCount > 0
      ? `${totalCount} active · ${overdueCount} overdue`
      : `${totalCount} active`;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Maintenance Schedules"
        tag={tagText}
        actions={
          isAdmin ? (
            <div className="flex items-center gap-2">
              <Link to="/settings/maintenance" title="Maintenance settings" className="text-slate-400 hover:text-slate-600 p-1">
                <Settings size={18} />
              </Link>
              <Button size="sm" onClick={openCreate}><Plus size={14} /> Add Task</Button>
            </div>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-canvas-line bg-white rounded-lg w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <Select
          value={filterActive}
          onChange={e => { setFilterActive(e.target.value as 'true' | 'false' | ''); setPage(1); }}
          className="py-1 text-xs"
        >
          <option value="true">Active only</option>
          <option value="false">Inactive only</option>
          <option value="">All</option>
        </Select>
        {(['Excel', 'CSV'] as const).map(fmt => (
                <Button
                  key={fmt}
                  variant="outline" size="sm"
                  onClick={async () => {
                    const all = await tasksApi.list(1, 5000, undefined, undefined,
                      filterActive === '' ? undefined : filterActive === 'true',
                      search || undefined);
                    const rows = all.data.map(t => ({
                      name:                   t.name,
                      task_type:              t.task_type,
                      equipment:              t.equipment?.name ?? t.equipment_id,
                      frequency_type:         t.frequency_type,
                      frequency_value:        t.frequency_value ?? '',
                      estimated_duration_min: t.estimated_duration_minutes ?? '',
                      next_due_at:            t.next_due_at ? new Date(t.next_due_at).toLocaleDateString() : '',
                      last_completed_at:      t.last_completed_at ? new Date(t.last_completed_at).toLocaleDateString() : '',
                      assigned_technician:    t.assigned_technician?.full_name ?? '',
                      is_active:              t.is_active ? 'true' : 'false',
                    }));
                    fmt === 'Excel' ? exportToExcel(rows, 'maintenance_schedules_export') : exportToCsv(rows, 'maintenance_schedules_export');
                  }}
                >
                  <Download size={14} /> {fmt}
                </Button>
              ))}
        {isAdmin && (
          <MaintenanceTaskImport
            onDownloadTemplate={downloadMaintenanceTaskTemplate}
            onComplete={() => qc.invalidateQueries({ queryKey: ['tasks'] })}
          />
        )}
      </div>

      {isLoading ? (
        <Card><CardBody><p className="text-center text-slate-400 py-6">Loading…</p></CardBody></Card>
      ) : data?.data.length === 0 ? (
        <Card><CardBody><p className="text-center text-slate-400 text-sm py-6">No maintenance tasks found.</p></CardBody></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data?.data.map(t => {
            const overdue   = isDue(t);
            const isMeter   = t.schedule_type === 'meter';
            const frequency = t.frequency_type === 'on_condition'
              ? 'On condition'
              : isMeter
                ? `Every ${t.meter_interval} ${t.frequency_type}`
                : `Every ${t.frequency_value} ${t.frequency_type}`;
            return (
              <div
                key={t.id}
                onClick={() => setDetail(t)}
                className="group bg-white rounded-xl border border-canvas-line shadow-[0_1px_0_#fff_inset,0_1px_2px_rgba(11,18,32,.04)] p-4 cursor-pointer transition-shadow hover:shadow-[0_1px_0_#fff_inset,0_8px_24px_-12px_rgba(11,18,32,.18)]"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                      {t.task_type} · {frequency}
                      {t.pm_group_id && <span className="text-indigo-500"> · Line</span>}
                    </p>
                    <h3 className="text-base font-semibold text-ink-900 mt-1 truncate">{t.name}</h3>
                    <p className="text-sm text-ink-500 mt-0.5 truncate">
                      {t.equipment?.asset_tag && <span className="font-mono text-xs mr-2">{t.equipment.asset_tag}</span>}
                      {t.equipment?.name ?? 'No equipment'}
                    </p>
                  </div>
                  {!t.is_active ? (
                    <Badge color="slate">Inactive</Badge>
                  ) : overdue ? (
                    <Badge color="red">Overdue</Badge>
                  ) : t.next_due_at ? (
                    <Badge color={taskTypeColor(t.task_type)}>{t.task_type}</Badge>
                  ) : null}
                </div>

                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-ink-500 mt-3">
                  {isMeter ? (
                    <>
                      {t.equipment?.current_reading != null && (
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden>⚙️</span>
                          Meter: <span className="font-mono text-ink-700">
                            {Number(t.equipment.current_reading).toLocaleString()}
                            {t.equipment.meter_unit ? ` ${t.equipment.meter_unit}` : ''}
                          </span>
                        </span>
                      )}
                      {t.last_trigger_reading != null && t.meter_interval != null && (
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden>🎯</span>
                          Trigger at: <span className="font-mono text-ink-700">
                            {(Number(t.last_trigger_reading) + Number(t.meter_interval)).toLocaleString()}
                          </span>
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {overdue
                        ? <AlertCircle size={13} className="text-red-500" />
                        : <span aria-hidden>📅</span>}
                      <span className={overdue ? 'text-red-600 font-medium' : ''}>
                        Next: <span className="font-mono text-ink-700">{fmtDateTime(t.next_due_at, timezone) || '—'}</span>
                      </span>
                    </span>
                  )}
                  {t.estimated_duration_minutes && (
                    <span className="inline-flex items-center gap-1.5">
                      <span aria-hidden>⏱️</span>
                      ~{t.estimated_duration_minutes} min
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden>👤</span>
                    {t.assigned_technician?.full_name ?? 'Unassigned'}
                  </span>
                </div>

                {(isSupervisor || isAdmin) && (
                  <div
                    className="flex items-center gap-1 flex-wrap mt-3 pt-3 border-t border-canvas-line"
                    onClick={e => e.stopPropagation()}
                  >
                    {isSupervisor && (
                      <button
                        onClick={() => createWO.mutate(t.id)}
                        disabled={createWO.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-brand-50 text-brand-600 disabled:opacity-40"
                        title="Create work order now"
                      >
                        <ClipboardPlus size={12} /> New WO
                      </button>
                    )}
                    {isAdmin && (
                      <>
                        <button onClick={() => openEdit(t)} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-slate-100 text-slate-600">
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete "${t.name}"?`)) del.mutate(t.id); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}

      {createWoError && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex justify-between items-center">
          <span>{createWoError}</span>
          <button onClick={() => setCreateWoError(null)} className="ml-4 text-red-400 hover:text-red-600 font-bold">✕</button>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title="Task Details" className="max-w-xl">
        {detail && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Task Name</p><p className="font-medium">{detail.name}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Type</p><Badge color={taskTypeColor(detail.task_type)}>{detail.task_type}</Badge></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Equipment</p><p>{detail.equipment?.name ?? '—'}<span className="text-slate-500 text-xs ml-1">{detail.equipment?.asset_tag}</span></p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Location</p><p>{detail.equipment?.location ?? '—'}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Frequency</p><p>{detail.frequency_type === 'on_condition' ? 'On condition' : `Every ${detail.frequency_value} ${detail.frequency_type}`}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Est. Duration</p><p>{detail.estimated_duration_minutes ? `${detail.estimated_duration_minutes} min` : '—'}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Next Due</p>
                <p className={isDue(detail) ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                  {isDue(detail) && <AlertCircle size={13} />}
                  {fmtDateTime(detail.next_due_at, timezone) || '—'}
                </p>
              </div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Last Completed</p><p>{fmtDateTime(detail.last_completed_at, timezone) || '—'}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Assigned To</p><p>{detail.assigned_technician?.full_name ?? 'Unassigned'}</p></div>
              <div><p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-0.5">Status</p><Badge color={detail.is_active ? 'green' : 'slate'}>{detail.is_active ? 'Active' : 'Inactive'}</Badge></div>
            </div>
            {detail.instructions && (
              <div>
                <p className="text-xs text-slate-600 font-medium uppercase tracking-wide mb-1">Instructions</p>
                <p className="whitespace-pre-wrap text-slate-700 bg-slate-50 rounded-lg p-3 text-xs leading-relaxed">{detail.instructions}</p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              {isSupervisor && (
                <Button variant="outline" size="sm" onClick={() => { createWO.mutate(detail.id); setDetail(null); }}>
                  <ClipboardPlus size={14} /> Create Work Order
                </Button>
              )}
              {isAdmin && (
                <Button size="sm" onClick={() => { setDetail(null); openEdit(detail); }}>
                  <Pencil size={14} /> Edit
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setDetail(null)}>Close</Button>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog open={open} onClose={closeDialog} title={editing ? 'Edit Task' : 'New Maintenance Task'} className="max-w-xl">
        <form onSubmit={e => { e.preventDefault(); save.mutate(); }} className="flex flex-col gap-3">
          <Input label="Task Name *" value={form.name ?? ''} onChange={e => set('name', e.target.value)} required />

          {/* Schedule type toggle — Pro feature for meter-based */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Schedule Type *</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => set('schedule_type', 'time')}
                className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${
                  form.schedule_type !== 'meter'
                    ? 'bg-brand-50 border-brand-500 text-brand-700 font-medium'
                    : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Time-based
              </button>
              <button
                type="button"
                onClick={() => hasPro && set('schedule_type', 'meter')}
                disabled={!hasPro}
                title={hasPro ? '' : 'Meter-based PMs require Pro tier'}
                className={`flex-1 px-3 py-2 text-sm rounded-md border transition-colors ${
                  form.schedule_type === 'meter'
                    ? 'bg-brand-50 border-brand-500 text-brand-700 font-medium'
                    : !hasPro
                      ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Meter-based {!hasPro && '· Pro'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select label="Task Type *" value={form.task_type ?? 'preventive'} onChange={e => set('task_type', e.target.value)}>
              <option value="preventive">Preventive</option>
              <option value="predictive">Predictive</option>
              <option value="corrective">Corrective</option>
            </Select>
            {editing ? (
              <Select label="Equipment *" value={form.equipment_id ?? ''} onChange={e => set('equipment_id', e.target.value)} required>
                <option value="">Select equipment…</option>
                {equipment?.data.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.asset_tag} — {eq.name}</option>
                ))}
              </Select>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Equipment * <span className="text-slate-400 font-normal">({equipmentIds.length} selected)</span>
                </label>
                {(() => {
                  const q = equipFilter.trim().toLowerCase();
                  const shown = (equipment?.data ?? []).filter(eq =>
                    !q ||
                    eq.name?.toLowerCase().includes(q) ||
                    eq.asset_tag?.toLowerCase().includes(q) ||
                    eq.location?.toLowerCase().includes(q),
                  );
                  const shownIds = shown.map(e => e.id);
                  const allShownSelected = shownIds.length > 0 && shownIds.every(id => equipmentIds.includes(id));
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-1.5">
                        <input
                          type="text"
                          value={equipFilter}
                          onChange={e => setEquipFilter(e.target.value)}
                          placeholder="Filter by location, name, or tag… (e.g. Line 1)"
                          className="flex-1 text-sm px-2.5 py-1.5 rounded-md border border-slate-700 bg-transparent"
                        />
                        <button
                          type="button"
                          className="text-xs text-indigo-500 hover:text-indigo-400 whitespace-nowrap"
                          onClick={() => setEquipmentIds(ids =>
                            allShownSelected ? ids.filter(id => !shownIds.includes(id)) : [...new Set([...ids, ...shownIds])],
                          )}
                        >
                          {allShownSelected ? 'Clear shown' : 'Select all shown'}
                        </button>
                      </div>
                      <div className="max-h-44 overflow-y-auto rounded-md border border-slate-700 divide-y divide-slate-800">
                        {shown.length === 0 ? (
                          <p className="text-xs text-slate-400 px-3 py-3">No equipment matches “{equipFilter}”.</p>
                        ) : shown.map(eq => {
                          const checked = equipmentIds.includes(eq.id);
                          return (
                            <label key={eq.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-800/50">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setEquipmentIds(ids => checked ? ids.filter(i => i !== eq.id) : [...ids, eq.id])}
                              />
                              <span className="text-sm">
                                {eq.asset_tag && <span className="font-mono text-xs mr-1 text-slate-400">{eq.asset_tag}</span>}
                                {eq.name}
                                {eq.location && <span className="text-slate-500 text-xs ml-1">· {eq.location}</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
                <p className="text-xs text-slate-400 mt-1">
                  Filter by location to pick a whole line, then “Select all shown”. Each machine gets its own PM and work orders.
                </p>
              </div>
            )}

            {editing?.pm_group_id && (
              <label className="flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={applyToGroup} onChange={e => setApplyToGroup(e.target.checked)} />
                <span>
                  Apply to all machines in this line
                  <span className="block text-xs text-slate-500">
                    Updates every machine's PM together (set Status to Inactive to deactivate the whole line). Equipment and meter settings aren't changed.
                  </span>
                </span>
              </label>
            )}

            {form.schedule_type === 'meter' ? (
              <>
                <Input
                  label="Meter Interval *"
                  type="number" min={1} step="any"
                  value={form.meter_interval ?? ''}
                  onChange={e => set('meter_interval', e.target.value ? parseFloat(e.target.value) : undefined)}
                  required
                  placeholder="e.g. 500 hours"
                />
                <Input
                  label="Start Reading"
                  type="number" min={0} step="any"
                  value={form.last_trigger_reading ?? ''}
                  onChange={e => set('last_trigger_reading', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="Current reading at creation (default 0)"
                />
              </>
            ) : (
              <>
                <Select
                  label="Frequency Type *"
                  value={form.frequency_type ?? 'days'}
                  onChange={e => {
                    const v = e.target.value;
                    setForm(f => ({ ...f, frequency_type: v, ...(v !== 'months' ? { month_week: null, month_weekday: null } : {}) }));
                  }}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="hours_run">Hours Run</option>
                  <option value="on_condition">On Condition</option>
                </Select>
                {form.frequency_type !== 'on_condition' && (
                  <Input
                    label="Frequency Value *"
                    type="number" min={1}
                    value={form.frequency_value ?? ''}
                    onChange={e => set('frequency_value', parseInt(e.target.value) || undefined)}
                    required
                  />
                )}
                {form.frequency_type === 'months' && (
                  <Select
                    label="Monthly on"
                    value={form.month_week != null && form.month_weekday != null ? 'nth' : 'dom'}
                    onChange={e => {
                      if (e.target.value === 'nth') setForm(f => ({ ...f, month_week: f.month_week ?? 1, month_weekday: f.month_weekday ?? 1 }));
                      else setForm(f => ({ ...f, month_week: null, month_weekday: null }));
                    }}
                  >
                    <option value="dom">The same day of the month</option>
                    <option value="nth">A chosen weekday (avoids weekends)</option>
                  </Select>
                )}
                {form.frequency_type === 'months' && form.month_week != null && form.month_weekday != null && (
                  <div className="grid grid-cols-2 gap-2">
                    <Select label="Which" value={String(form.month_week)} onChange={e => set('month_week', parseInt(e.target.value))}>
                      <option value="1">First</option>
                      <option value="2">Second</option>
                      <option value="3">Third</option>
                      <option value="4">Fourth</option>
                      <option value="-1">Last</option>
                    </Select>
                    <Select label="Weekday" value={String(form.month_weekday)} onChange={e => set('month_weekday', parseInt(e.target.value))}>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                      <option value="0">Sunday</option>
                    </Select>
                  </div>
                )}
              </>
            )}
            <Input
              label="Est. Duration (min)"
              type="number" min={1}
              value={form.estimated_duration_minutes ?? ''}
              onChange={e => set('estimated_duration_minutes', e.target.value ? parseInt(e.target.value) : undefined)}
            />
            {hasPro && (
              <Input
                label="Compliance grace (days)"
                type="number" min={0}
                value={form.compliance_window_days ?? ''}
                onChange={e => set('compliance_window_days', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Blank = plant default"
              />
            )}
            {isAdmin && users && (
              <Select label="Assigned Technician" value={form.assigned_technician_id ?? ''} onChange={e => set('assigned_technician_id', e.target.value)}>
                <option value="">Unassigned</option>
                {users.data.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </Select>
            )}
            <Select label="Status" value={form.is_active ? 'true' : 'false'} onChange={e => set('is_active', e.target.value === 'true')}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </div>
          <Textarea label="Instructions" value={form.instructions ?? ''} onChange={e => set('instructions', e.target.value)} />
          {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" disabled={save.isPending || (!editing && equipmentIds.length === 0)}>
              {save.isPending ? 'Saving…' : (!editing && equipmentIds.length > 1 ? `Create ${equipmentIds.length} PMs` : 'Save')}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
